/**
 * A fonte única dos tokens de tema da Tãmi.
 *
 * Antes deste arquivo existiam três cópias — o `globals.css` do web, o
 * `global.css` do mobile e um `theme.ts` — mantidas por um teste de paridade
 * que comparava app com app. O teste chegou a ficar vermelho sem ninguém ver:
 * `warning` entrou no web e nunca chegou ao mobile.
 *
 * Aqui é onde se muda cor. O CSS do mobile é gerado a partir daqui e conferido
 * por teste; o do web ainda é conferido, e passa a ser gerado na Metade 2.
 */

export const TOKENS = {
  light: {
    background: "47.14 0% 100%",
    foreground: "0 0% 3.9%",
    muted: "0 0% 96.1%",
    "muted-foreground": "0 0% 45.1%",
    popover: "0 0% 100%",
    "popover-foreground": "0 0% 3.9%",
    card: "0 0% 93.33%",
    "card-foreground": "0 0% 11.37%",
    border: "0 0% 87.45%",
    input: "0 0% 87.45%",
    primary: "0 0% 11.37%",
    "primary-foreground": "47.14 77.78% 92.94%",
    secondary: "71.19 89.33% 70.59%",
    "secondary-foreground": "0 0% 0%",
    accent: "0 0% 87.45%",
    "accent-foreground": "0 0% 0%",
    destructive: "0 78.12% 46.64%",
    "destructive-foreground": "0 0% 98%",
    ring: "0 0% 3.9%",
    "chart-1": "257.49 93.58% 63.33%",
    "chart-2": "70.95 60.16% 48.24%",
    "chart-3": "20.5 95.28% 58.43%",
    "chart-4": "346.31 83.24% 64.9%",
    "chart-5": "47.14 0% 0%",
    "chart-6": "205.14 94% 63%",
    "gradient-foreground": "0 0% 3.9%",
    brand: "257.49 93.58% 63.33%",
    "brand-foreground": "0 0% 100%",
    highlight: "71.19 89.33% 70.59%",
    "highlight-foreground": "0 0% 0%",
    /**
     * Destaque como cor de texto/traço sobre o fundo do tema. No claro o lima
     * some (1,9:1 no card), então vira roxo escurecido; no escuro segue o lima.
     */
    "highlight-text": "257.49 80% 52%",
    warning: "20.5 95.28% 58.43%",
    "warning-foreground": "24 92% 8%",
  },
  dark: {
    background: "0 0% 3.9%",
    foreground: "0 0% 100%",
    muted: "0 0% 9.23%",
    "muted-foreground": "0 0% 65.88%",
    popover: "0 0% 3.9%",
    "popover-foreground": "0 0% 98%",
    card: "0 0% 14.12%",
    "card-foreground": "0 0% 100%",
    border: "0 0% 27.84%",
    input: "0 0% 27.84%",
    primary: "47.14 77.78% 92.94%",
    "primary-foreground": "0 0% 14.9%",
    secondary: "71.19 89.33% 70.59%",
    "secondary-foreground": "0 0% 0%",
    accent: "0 0% 27.45%",
    "accent-foreground": "0 0% 100%",
    destructive: "0 59.44% 48.73%",
    "destructive-foreground": "0 0% 98%",
    ring: "47.14 77.78% 92.94%",
    "chart-1": "257.49 93.58% 63.33%",
    "chart-2": "71.19 89.33% 70.59%",
    "chart-3": "20.5 95.28% 58.43%",
    "chart-4": "346.31 83.24% 64.9%",
    "chart-5": "47.14 77.78% 92.94%",
    "chart-6": "205.14 94% 63%",
    "gradient-foreground": "0 0% 3.9%",
    brand: "257.49 93.58% 63.33%",
    "brand-foreground": "0 0% 100%",
    highlight: "71.19 89.33% 70.59%",
    "highlight-foreground": "0 0% 0%",
    "highlight-text": "71.19 89.33% 70.59%",
    warning: "20.5 95.28% 58.43%",
    "warning-foreground": "24 92% 8%",
  },
} as const;

export type ColorScheme = keyof typeof TOKENS;
export type Token = keyof (typeof TOKENS)["light"];

/** Tokens que existem só no web. Um token novo fora desta lista quebra o teste. */
export const WEB_ONLY = {
  radius: "virou número literal no tailwind.config — React Native não tem calc()",
  "sidebar-background": "não há sidebar em tela nativa",
  "sidebar-foreground": "não há sidebar em tela nativa",
  "sidebar-primary": "não há sidebar em tela nativa",
  "sidebar-primary-foreground": "não há sidebar em tela nativa",
  "sidebar-accent": "não há sidebar em tela nativa",
  "sidebar-accent-foreground": "não há sidebar em tela nativa",
  "sidebar-border": "não há sidebar em tela nativa",
  "sidebar-ring": "não há sidebar em tela nativa",
  "sidebar-width": "não há sidebar em tela nativa",
  vvh: "viewport virtual é problema de navegador",
  "vv-offset-top": "viewport virtual é problema de navegador",
  "vv-keyboard-inset": "viewport virtual é problema de navegador",
  "mobile-bottom-bar-height": "a barra inferior nativa é do expo-router",
  "mobile-bottom-bar-bottom-padding": "a barra inferior nativa é do expo-router",
  "mobile-bottom-bar-clearance": "a barra inferior nativa é do expo-router",
} as const;

/**
 * Tokens que só o mobile tem. `brand` e `highlight` no web não declaram par de
 * contraste porque lá o texto sobre eles é escolhido caso a caso; aqui todo
 * texto passa pelo `<Text>` e precisa de um token para apontar.
 */
export const MOBILE_ONLY = {
  "brand-foreground": "o <Text> precisa de um token de contraste sobre bg-brand",
  "highlight-foreground": "o <Text> precisa de um token de contraste sobre bg-highlight",
} as const;

/** `hsl("background", "dark")` → `"hsl(0 0% 3.9%)"` */
export function hsl(token: Token, scheme: ColorScheme): string {
  return `hsl(${TOKENS[scheme][token]})`;
}

/**
 * `"0 0% 3.9%"` → `"#0A0A0A"`. Só para APIs que exigem hex e não aceitam
 * string de cor CSS — hoje, a conferência do `app.json` no teste de paridade.
 */
export function hslToHex(value: string): string {
  const [hue, saturation, lightness] = value
    .split(" ")
    .map((part) => Number.parseFloat(part)) as [number, number, number];

  const s = saturation / 100;
  const l = lightness / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;

  const [r, g, b] = (
    hue < 60
      ? [c, x, 0]
      : hue < 120
        ? [x, c, 0]
        : hue < 180
          ? [0, c, x]
          : hue < 240
            ? [0, x, c]
            : hue < 300
              ? [x, 0, c]
              : [c, 0, x]
  ).map((channel) =>
    Math.round((channel + m) * 255)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase(),
  );

  return `#${r}${g}${b}`;
}

/**
 * `hexToRgba("#1D1D1D", 0.3)` → `"rgba(29, 29, 29, 0.3)"`. Para sombra e brilho
 * em estilo inline, onde a classe do Tailwind com `/30` não chega.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const digits = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((start) =>
    Number.parseInt(digits.slice(start, start + 2), 16),
  );
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * `darkenHex("#848484", 0.7)` → `"#5c5c5c"`. Escurece um tom da paleta por um
 * fator (0–1) — para quando um tom não bate contraste mínimo contra um fundo
 * claro e a spec permite escurecer o tom existente em vez de inventar um novo.
 *
 * Aceita hex de 3 dígitos além do de 6 (`"#fff"` vira `"ffffff"` antes de
 * escurecer, a mesma expansão do CSS); sem isso, `"#fff".slice(4, 6)` fica
 * vazio e o canal azul sai `NaN`. Sempre devolve 6 dígitos minúsculos.
 */
export function darkenHex(hex: string, factor: number): string {
  const shorthand = hex.replace("#", "");
  const digits =
    shorthand.length === 3
      ? shorthand
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : shorthand;
  const channels = [0, 2, 4].map((start) => {
    const value = Number.parseInt(digits.slice(start, start + 2), 16);
    return Math.round(value * factor)
      .toString(16)
      .padStart(2, "0");
  });
  return `#${channels.join("")}`;
}
